/**
 * Error Type Definitions
 * 
 * Custom error types for the application
 */

/**
 * Budget Exceeded Error
 * 
 * Thrown when AI parsing budget is exceeded (402 response from backend)
 */
export class BudgetExceededError extends Error {
  public readonly code = 'budget_exceeded';
  public readonly retryAfter: number; // seconds until retry
  
  constructor(retryAfter: number, message?: string) {
    super(message || 'AI parsing budget exceeded. Please try again later.');
    this.name = 'BudgetExceededError';
    this.retryAfter = retryAfter;
    
    // Maintains proper stack trace for where our error was thrown (only available on V8)
    // Type assertion needed because captureStackTrace is V8-specific and not in standard TypeScript types
    interface ErrorConstructorWithCaptureStackTrace extends ErrorConstructor {
      captureStackTrace?: (error: Error, constructorOpt?: typeof BudgetExceededError) => void;
    }
    const ErrorWithCapture = Error as ErrorConstructorWithCaptureStackTrace;
    if (ErrorWithCapture.captureStackTrace) {
      ErrorWithCapture.captureStackTrace(this, BudgetExceededError);
    }
  }
  
  /**
   * Get retry date/time as a Date object
   */
  getRetryDate(): Date {
    return new Date(Date.now() + this.retryAfter * 1000);
  }
  
  /**
   * Get formatted retry time string
   */
  getRetryTimeString(): string {
    const retryDate = this.getRetryDate();
    const now = new Date();
    const diffMs = retryDate.getTime() - now.getTime();
    
    if (diffMs < 60000) {
      // Less than 1 minute
      return 'in a few moments';
    } else if (diffMs < 3600000) {
      // Less than 1 hour
      const minutes = Math.ceil(diffMs / 60000);
      return `in ${minutes} minute${minutes !== 1 ? 's' : ''}`;
    } else if (diffMs < 86400000) {
      // Less than 1 day
      const hours = Math.ceil(diffMs / 3600000);
      return `in ${hours} hour${hours !== 1 ? 's' : ''}`;
    } else {
      // More than 1 day
      const days = Math.ceil(diffMs / 86400000);
      return `in ${days} day${days !== 1 ? 's' : ''}`;
    }
  }
}
